import { Coins } from "lucide-react";
import Flag from "react-world-flags";

import {
  CURRENCY_TO_DISPERSION_CODE,
  CURRENCY_TO_FLAG_CODE,
} from "@/modules/dashboard/wallets/constants/wallets.constants";
import { ColocaMoneyTransit } from "@/shared/components/motion/ColocaMoneyTransit";
import useTranslations from "@/shared/lang/useTranslations";
import { formatRail } from "@/shared/utils/railDisplay";

import { PAYOUT_WIZARD_COUNTRIES } from "../../constants/dispersions.constants";
import { isCrossCurrencySend, type SendRoute } from "../../utils/walletSend";
import { formatSendMoney, formatSendMoneyWithCode } from "./formatSendMoney";

export interface SendMoneyTransitProps {
  route: SendRoute;
  /** Who receives it, as the review named them. */
  recipientName: string;
  /** Leaves the wallet, in the origin currency. */
  debit: string;
  /** Reaches the person, in the delivery currency. */
  arrives: string;
  className?: string;
}

function FlagTile({ code }: { code: string | null }) {
  return code ? (
    <Flag code={code} className="h-5 w-7 rounded-[3px] object-cover shadow-sm" aria-hidden />
  ) : (
    <Coins className="size-5" aria-hidden="true" />
  );
}

/**
 * A send on its way, drawn from its own route: pesos leave the COP wallet and land in Colombia as pesos; on a
 * converted send they cross Coloca, where the coin changes face ($ → R$), and land in the corridor's
 * currency. It loops while the payout waits for the destination bank, and says nothing about progress.
 */
export function SendMoneyTransit({ route, recipientName, debit, arrives, className }: SendMoneyTransitProps) {
  const { t, activeLanguage } = useTranslations("dispersions");
  const country = PAYOUT_WIZARD_COUNTRIES.find((candidate) => candidate.code === route.country);
  const originFlag =
    CURRENCY_TO_FLAG_CODE[route.originCurrency] ?? CURRENCY_TO_DISPERSION_CODE[route.originCurrency] ?? null;
  const converts = isCrossCurrencySend(route);
  const amountOut = formatSendMoneyWithCode(debit, route.originCurrency, activeLanguage);
  const amountIn = formatSendMoneyWithCode(arrives, route.currency, activeLanguage);

  return (
    <ColocaMoneyTransit
      className={className}
      label={t(converts ? "send_transit_aria_fx" : "send_transit_aria", {
        debit: amountOut,
        amount: amountIn,
        name: recipientName,
        country: country?.name ?? route.country,
      })}
      from={{
        icon: <FlagTile code={originFlag} />,
        label: t("send_transit_from", { currency: route.originCurrency }),
        // The label already names the currency, and the tile is narrow: the figure alone.
        caption: formatSendMoney(debit, route.originCurrency, activeLanguage),
      }}
      to={{
        icon: <FlagTile code={country?.flag ?? null} />,
        label: recipientName,
        // The flag already says the country.
        caption: formatRail(route.rail),
      }}
      legs={
        converts
          ? [
              { rail: { kind: "fiat" }, asset: { kind: "fiat", currency: route.originCurrency } },
              { rail: { kind: "fiat" }, asset: { kind: "fiat", currency: route.currency } },
            ]
          : [{ rail: { kind: "fiat" }, asset: { kind: "fiat", currency: route.currency } }]
      }
    />
  );
}
